import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogEntry } from '@couch/shared';
import { api, errorMessage } from '../../api/client';
import { downloadsKey } from './DownloadsPanel';
import { formatBytes } from './format';
import { useDeleteModel } from './useDeleteModel';

const FIT_TEXT: Readonly<Record<CatalogEntry['fit'], string>> = Object.freeze({
  good: 'Fits well', tight: 'Fits — close other apps', too_big: 'Too big for this machine', unknown: 'Fit unknown',
});

function EntryCard({ machineId, entry }: { machineId: string; entry: CatalogEntry }) {
  const queryClient = useQueryClient();
  const install = useMutation({
    mutationFn: () => api.installCatalogEntry(machineId, entry.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: downloadsKey(machineId) }),
  });
  const { ask, done, remove } = useDeleteModel(machineId);
  return (
    <article className="catalog-card" aria-labelledby={`entry-${entry.id}`}>
      <h3 id={`entry-${entry.id}`}>{entry.title}</h3>
      <p className="muted">{entry.description}</p>
      <p className="tags">
        {entry.recommended && <span className="badge badge-pending">Recommended</span>}
        <span className={`badge badge-fit-${entry.fit}`}>{FIT_TEXT[entry.fit]}</span>
        {entry.requires.length > 0 && <span className="muted">Needs {entry.requires.join(', ')}</span>}
      </p>
      {entry.installed ? (
        <p className="catalog-installed">
          <span className="success">Installed</span>
          <button type="button" className="button-quiet" disabled={remove.isPending} aria-label={`Delete ${entry.title} (${formatBytes(entry.sizeBytes)})`}
            onClick={() => { ask({ folder: entry.folder, name: entry.fileName, label: entry.title, sizeBytes: entry.sizeBytes }); }}>Delete</button>
        </p>
      ) : (
        <button type="button" disabled={install.isPending || install.isSuccess} aria-label={`Install ${entry.title} (${formatBytes(entry.sizeBytes)})`} onClick={() => { install.mutate(); }}>
          {install.isSuccess ? 'Downloading…' : `Install · ${formatBytes(entry.sizeBytes)}`}
        </button>
      )}
      {install.isError && <p role="alert" className="form-error">{errorMessage(install.error)}</p>}
      {done && <p role="status">{done}</p>}
      {remove.isError && <p role="alert" className="form-error">{errorMessage(remove.error)}</p>}
    </article>
  );
}

export function CatalogPanel({ machineId }: { machineId: string }) {
  const catalog = useQuery({ queryKey: ['catalog', machineId], queryFn: () => api.machineCatalog(machineId), retry: false });
  return (
    <section className="panel" aria-labelledby="catalog-title">
      <h2 id="catalog-title">Starter models</h2>
      <p className="muted">
        All models on this machine, your own downloads included, are listed under <a href="#models-title">Installed models</a> below,
        grouped by model family.
      </p>
      {catalog.isPending && <p role="status">Loading…</p>}
      {catalog.isError && <p role="alert" className="form-error">{errorMessage(catalog.error)}</p>}
      <div className="catalog-grid">{catalog.data?.entries.map((e) => <EntryCard key={e.id} machineId={machineId} entry={e} />)}</div>
    </section>
  );
}
